import { get, post, patch, del } from "@shared/utils/api";

const BASE = "/api/v1/user_management/customers";

function buildUrl(slug, resource, params = {}) {
  const search = new URLSearchParams();
  if (params.query != null && params.query !== "") search.set("query", params.query);
  if (params.page != null) search.set("page", String(params.page));
  if (params.per_page != null) search.set("per_page", String(params.per_page));
  if (params.sort != null && params.sort !== "") search.set("sort", params.sort);
  if (params.order != null && params.order !== "") search.set("order", params.order);
  const qs = search.toString();
  return `${BASE}/${encodeURIComponent(slug)}/${resource}${qs ? `?${qs}` : ""}`;
}

function userUrl(slug, userId) {
  return `${BASE}/${encodeURIComponent(slug)}/users/${encodeURIComponent(userId)}`;
}

function roleUrl(slug, roleId) {
  return `${BASE}/${encodeURIComponent(slug)}/roles/${encodeURIComponent(roleId)}`;
}

function buildAppScopedUrl(slug, appSlug, resource, params = {}) {
  const search = new URLSearchParams();
  if (params.query != null && params.query !== "") search.set("query", params.query);
  if (params.page != null) search.set("page", String(params.page));
  if (params.per_page != null) search.set("per_page", String(params.per_page));
  if (params.sort != null && params.sort !== "") search.set("sort", params.sort);
  if (params.order != null && params.order !== "") search.set("order", params.order);
  const qs = search.toString();
  return `${BASE}/${encodeURIComponent(slug)}/apps/${encodeURIComponent(appSlug)}/${resource}${qs ? `?${qs}` : ""}`;
}

function roleUrlAppScoped(slug, appSlug, roleId) {
  return `${BASE}/${encodeURIComponent(slug)}/apps/${encodeURIComponent(appSlug)}/roles/${encodeURIComponent(roleId)}`;
}

function permissionGroupUrlAppScoped(slug, appSlug, groupId) {
  return `${BASE}/${encodeURIComponent(slug)}/apps/${encodeURIComponent(appSlug)}/permission_groups/${encodeURIComponent(groupId)}`;
}

function normalizeListResponse(body, dataKey, totalKey = "count") {
  const data = body[dataKey] ?? [];
  const pageSize = body[totalKey] ?? data.length;
  const totalCount = body.total_count ?? body.meta?.total_count ?? pageSize;
  const page = body.meta?.page ?? 1;
  const perPage = body.meta?.per_page ?? (pageSize > 0 ? pageSize : 10);
  const totalPages = body.meta?.total_pages ?? (perPage > 0 ? Math.ceil(totalCount / perPage) : 1);
  return {
    data,
    meta: {
      page,
      per_page: perPage,
      total_count: totalCount,
      total_pages: totalPages,
    },
  };
}

export async function isUserManagementConfigured(slug) {
  const url = buildUrl(slug, "users", { per_page: 1 });
  try {
    const response = await get(url);
    if (response.status === 503) return false;
    const body = await response.json().catch(() => ({}));
    if (body.error && /database not configured|not configured/i.test(body.error)) return false;
    return true;
  } catch {
    return false;
  }
}

export async function fetchUserManagementUsers(slug, params = {}) {
  const url = buildUrl(slug, "users", params);
  const response = await get(url);
  if (!response.ok) {
    const err = new Error(response.status === 403 ? "Forbidden" : "Failed to load users");
    err.status = response.status;
    throw err;
  }
  const body = await response.json();
  return normalizeListResponse(body, "users");
}

export async function fetchUserManagementUser(slug, userId) {
  const response = await get(userUrl(slug, userId));
  if (!response.ok) {
    const err = new Error(response.status === 404 ? "User not found" : "Failed to load user");
    err.status = response.status;
    throw err;
  }
  return response.json();
}

export async function inviteUserManagementUser(slug, data) {
  const url = `${BASE}/${encodeURIComponent(slug)}/users/invite`;
  const response = await post(url, data);
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(body.message || body.error || "Failed to send invite");
    err.status = response.status;
    err.code = body.error;
    throw err;
  }
  return body;
}

export async function updateUserManagementUser(slug, userId, data) {
  const response = await patch(userUrl(slug, userId), data);
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const err = new Error(body.error || "Failed to update user");
    err.status = response.status;
    throw err;
  }
  return response.json();
}

export async function addUserRole(slug, userId, roleId) {
  const response = await post(`${userUrl(slug, userId)}/roles`, { role_id: roleId });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const err = new Error(body.error || "Failed to add role");
    err.status = response.status;
    throw err;
  }
  return response.json();
}

export async function removeUserRole(slug, userId, roleId) {
  const response = await del(`${userUrl(slug, userId)}/roles/${encodeURIComponent(roleId)}`);
  if (!response.ok) {
    const err = new Error("Failed to remove role");
    err.status = response.status;
    throw err;
  }
}

export async function fetchUserManagementRoles(slug, params = {}) {
  const url = buildUrl(slug, "roles", params);
  const response = await get(url);
  if (!response.ok) {
    const err = new Error(response.status === 403 ? "Forbidden" : "Failed to load roles");
    err.status = response.status;
    throw err;
  }
  const body = await response.json();
  return normalizeListResponse(body, "roles");
}

export async function fetchUserManagementRolesForApp(slug, appSlug, params = {}) {
  const url = buildAppScopedUrl(slug, appSlug, "roles", params);
  const response = await get(url);
  if (!response.ok) {
    const err = new Error(response.status === 403 ? "Forbidden" : "Failed to load roles");
    err.status = response.status;
    throw err;
  }
  const body = await response.json();
  return normalizeListResponse(body, "roles");
}

export async function fetchUserManagementRole(slug, roleId) {
  const response = await get(roleUrl(slug, roleId));
  if (!response.ok) {
    const err = new Error(response.status === 404 ? "Role not found" : "Failed to load role");
    err.status = response.status;
    throw err;
  }
  return response.json();
}

export async function fetchUserManagementRoleForApp(slug, appSlug, roleId) {
  const response = await get(roleUrlAppScoped(slug, appSlug, roleId));
  if (!response.ok) {
    const err = new Error(response.status === 404 ? "Role not found" : "Failed to load role");
    err.status = response.status;
    throw err;
  }
  return response.json();
}

export async function createUserManagementRole(slug, data) {
  const url = `${BASE}/${encodeURIComponent(slug)}/roles`;
  const response = await post(url, data);
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const err = new Error(body.error || "Failed to create role");
    err.status = response.status;
    throw err;
  }
  return response.json();
}

export async function createUserManagementRoleForApp(slug, appSlug, data) {
  const url = `${BASE}/${encodeURIComponent(slug)}/apps/${encodeURIComponent(appSlug)}/roles`;
  const response = await post(url, data);
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const err = new Error(body.error || "Failed to create role");
    err.status = response.status;
    throw err;
  }
  return response.json();
}

export async function updateUserManagementRole(slug, roleId, data) {
  const response = await patch(roleUrl(slug, roleId), data);
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const err = new Error(body.error || "Failed to update role");
    err.status = response.status;
    throw err;
  }
  return response.json();
}

export async function updateUserManagementRoleForApp(slug, appSlug, roleId, data) {
  const response = await patch(roleUrlAppScoped(slug, appSlug, roleId), data);
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const err = new Error(body.error || "Failed to update role");
    err.status = response.status;
    throw err;
  }
  return response.json();
}

export async function deleteUserManagementRole(slug, roleId) {
  const response = await del(roleUrl(slug, roleId));
  if (!response.ok) {
    const err = new Error("Failed to delete role");
    err.status = response.status;
    throw err;
  }
}

export async function deleteUserManagementRoleForApp(slug, appSlug, roleId) {
  const response = await del(roleUrlAppScoped(slug, appSlug, roleId));
  if (!response.ok) {
    const err = new Error("Failed to delete role");
    err.status = response.status;
    throw err;
  }
}

export async function addRolePermission(slug, roleId, permissionId) {
  const response = await post(`${roleUrl(slug, roleId)}/permissions`, { permission_id: permissionId });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const err = new Error(body.error || "Failed to add permission to role");
    err.status = response.status;
    throw err;
  }
  return response.json();
}

export async function addRolePermissionForApp(slug, appSlug, roleId, permissionId) {
  const response = await post(`${roleUrlAppScoped(slug, appSlug, roleId)}/permissions`, { permission_id: permissionId });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const err = new Error(body.error || "Failed to add permission to role");
    err.status = response.status;
    throw err;
  }
  return response.json();
}

export async function removeRolePermission(slug, roleId, permissionId) {
  const response = await del(`${roleUrl(slug, roleId)}/permissions/${encodeURIComponent(permissionId)}`);
  if (!response.ok) {
    const err = new Error("Failed to remove permission");
    err.status = response.status;
    throw err;
  }
}

export async function removeRolePermissionForApp(slug, appSlug, roleId, permissionId) {
  const response = await del(`${roleUrlAppScoped(slug, appSlug, roleId)}/permissions/${encodeURIComponent(permissionId)}`);
  if (!response.ok) {
    const err = new Error("Failed to remove permission");
    err.status = response.status;
    throw err;
  }
}

export async function fetchUserManagementPermissionGroupsForApp(slug, appSlug, params = {}) {
  const url = buildAppScopedUrl(slug, appSlug, "permission_groups", params);
  const response = await get(url);
  if (!response.ok) {
    const err = new Error(response.status === 403 ? "Forbidden" : "Failed to load permission groups");
    err.status = response.status;
    throw err;
  }
  const body = await response.json();
  return normalizeListResponse(body, "permission_groups");
}

export async function fetchUserManagementPermissionGroup(slug, groupId) {
  const url = `${BASE}/${encodeURIComponent(slug)}/permission_groups/${encodeURIComponent(groupId)}`;
  const response = await get(url);
  if (!response.ok) {
    const err = new Error(response.status === 404 ? "Permission group not found" : "Failed to load permission group");
    err.status = response.status;
    throw err;
  }
  return response.json();
}

export async function fetchUserManagementPermissionGroupForApp(slug, appSlug, groupId) {
  const response = await get(permissionGroupUrlAppScoped(slug, appSlug, groupId));
  if (!response.ok) {
    const err = new Error(response.status === 404 ? "Permission group not found" : "Failed to load permission group");
    err.status = response.status;
    throw err;
  }
  return response.json();
}

export async function fetchUserManagementPermissions(slug) {
  const url = `${BASE}/${encodeURIComponent(slug)}/permissions`;
  const response = await get(url);
  if (!response.ok) {
    const err = new Error("Failed to load permissions");
    err.status = response.status;
    throw err;
  }
  const body = await response.json();
  return body.permissions ?? [];
}

export async function fetchUserManagementPermissionsForApp(slug, appSlug) {
  const url = `${BASE}/${encodeURIComponent(slug)}/apps/${encodeURIComponent(appSlug)}/permissions`;
  const response = await get(url);
  if (!response.ok) {
    const err = new Error("Failed to load permissions");
    err.status = response.status;
    throw err;
  }
  const body = await response.json();
  return body.permissions ?? [];
}

export async function updateUserManagementPermissionGroup(slug, groupId, data) {
  const url = `${BASE}/${encodeURIComponent(slug)}/permission_groups/${encodeURIComponent(groupId)}`;
  const response = await patch(url, data);
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const err = new Error(body.error || "Failed to update permission group");
    err.status = response.status;
    throw err;
  }
  return response.json();
}

export async function updateUserManagementPermissionGroupForApp(slug, appSlug, groupId, data) {
  const response = await patch(permissionGroupUrlAppScoped(slug, appSlug, groupId), data);
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const err = new Error(body.error || "Failed to update permission group");
    err.status = response.status;
    throw err;
  }
  return response.json();
}

export async function createUserManagementPermissionGroup(slug, data) {
  const url = `${BASE}/${encodeURIComponent(slug)}/permission_groups`;
  const response = await post(url, data);
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const err = new Error(body.error || "Failed to create permission group");
    err.status = response.status;
    throw err;
  }
  return response.json();
}

export async function createUserManagementPermissionGroupForApp(slug, appSlug, data) {
  const url = `${BASE}/${encodeURIComponent(slug)}/apps/${encodeURIComponent(appSlug)}/permission_groups`;
  const response = await post(url, data);
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const err = new Error(body.error || "Failed to create permission group");
    err.status = response.status;
    throw err;
  }
  return response.json();
}
